import React from "react";

export default function SeoGuideSection() {
  return (
    <section className="stf-section">
      <div className="stf-wrap">
        <div className="stf-seo-grid">
          <article className="stf-article">
            <div className="stf-kicker">Guide</div>
            <h2>Hiring construction site security in Australia: what to know before you book</h2>
            <p>
              Construction sites carry the same legal requirement for security work as any other property in Australia, with one added layer: sites are often unattended for long stretches, and that's exactly when theft and break ins happen. The rules around who can work on your site and what they're allowed to do are set by your state, not by the head contractor and not by the security company quoting you.
            </p>
            <p>
              This guide covers what builders and developers most often get wrong when hiring site security, and what to check before you confirm anyone.
            </p>

            <h3 id="licence">Do you legally need a licensed security guard?</h3>
            <p>
              Yes, in every state. Patrolling, guarding plant and equipment, and monitoring site access all count as licensable security work, the same as any static guarding role.
            </p>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>License commonly required</th>
                  <th>Regulator</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>New South Wales</td>
                  <td>Security License Class 1A, Security Officer</td>
                  <td>NSW Police SLED</td>
                </tr>
                <tr>
                  <td>Victoria</td>
                  <td>Private Security Individual Operator License, security guard activity</td>
                  <td>Victoria Police Licensing and Regulation Division</td>
                </tr>
                <tr>
                  <td>Queensland</td>
                  <td>Security Provider License Class 1, unarmed security officer</td>
                  <td>Office of Fair Trading</td>
                </tr>
                <tr>
                  <td>Western Australia</td>
                  <td>Security Officer license</td>
                  <td>WA Police Licensing</td>
                </tr>
                <tr>
                  <td>South Australia</td>
                  <td>Security Agents License, general guarding function</td>
                  <td>Consumer &amp; Business Services</td>
                </tr>
              </tbody>
            </table>
            <div className="stf-callout">
              <b>Worth knowing:</b> many sites also require guards to hold a General Construction Induction card, commonly known as a White Card, before they're allowed past the gate, even though the guard isn't performing construction work themselves. Check your site's own access policy and mention it in your job post if it applies.
            </div>

            <h3 id="how-many">How much cover does your site need?</h3>
            <p>
              There's no single national ratio for construction. Most builders work from the site's stage and value at risk, then adjust for known problem hours.
            </p>
            <ul>
              <li><strong>Site stage:</strong> a slab with no fixtures carries different risk to a near complete build full of fittings and appliances.</li>
              <li><strong>Value on site:</strong> stored plant, tools and materials are the usual target, particularly copper and power tools.</li>
              <li><strong>Access points:</strong> every gate and breach point in the hoarding is a position that needs checking.</li>
              <li><strong>Hours of exposure:</strong> weeknights, weekends and the gap between trades finishing and the next crew starting are the highest risk windows.</li>
              <li><strong>History:</strong> a site that's already had a break in usually needs a heavier patrol schedule, not a lighter one.</li>
            </ul>
            <p>If your insurer has already set a minimum patrol frequency as a condition of cover, treat that as the floor, not the target.</p>

            <h3 id="cost">What construction site security costs</h3>
            <p>
              Rates for licensed site guards in Australia generally sit between $36 and $54 an hour, depending on the work. The biggest factors are overnight versus daytime, weekend versus weeknight, and static coverage versus a mobile patrol across several sites. Weekend overnight shifts sit at the top of that range.
            </p>
            <p>
              The other variable is who you book through. Agencies typically add a margin on top of the guard's rate for coordination and rostering. On Staffoo, guards set their own rate and you see it on their application, so the number you compare is the number you pay. You can see current averages in the pricing section above.
            </p>

            <h3 id="vs">Static guard or mobile patrol, which do you need?</h3>
            <p>
              Both are licensed the same way, the difference is how the coverage is delivered across your site or sites.
            </p>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Static Guard</th>
                  <th>Mobile Patrol</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Typical work</td>
                  <td>Stays on one site for the full shift, usually overnight.</td>
                  <td>Drives a scheduled route between several sites, checking each one for a set window.</td>
                </tr>
                <tr>
                  <td>Best for</td>
                  <td>Suits a single high value or near complete site.</td>
                  <td>Suits several smaller sites where a guard on each one isn't cost effective.</td>
                </tr>
                <tr>
                  <td>Response</td>
                  <td>On site immediately if something happens.</td>
                  <td>Relies on the scheduled round and alarm response in between.</td>
                </tr>
              </tbody>
            </table>

            <h3 id="checklist">What to check before you confirm a guard</h3>
            <ul>
              <li><strong>License class and expiry:</strong> that it matches the work and is current in your state.</li>
              <li><strong>Relevant experience:</strong> construction site experience is a different job to retail or office work.</li>
              <li><strong>Reviews from similar sites:</strong> volume matters less than whether the sites looked like yours.</li>
              <li><strong>Availability across the full roster:</strong> weekends and public holidays are when most sites are exposed.</li>
              <li><strong>Who's responsible for insurance:</strong> and what happens if a guard can't make a shift.</li>
            </ul>
            <p>
              On Staffoo, license verification happens before a guard can apply, and the license class, experience and review history sit on every application you receive.
            </p>

            <h3 id="direct">Hiring direct instead of through an agency</h3>
            <p>
              Traditional agencies take your brief, roster guards internally and send you an invoice. You rarely know who's coming until the day, and you can't request the same person twice. Hiring direct through a platform flips that: you see who applied, you pick, and you can rebook the guards who worked out well across your next site. For a developer running several builds at once, that continuity usually matters more than the hourly rate.
            </p>
            <p>
              If you're hiring for a different setting, the same process applies to corporate and office security, retail security and residential and estate security.
            </p>

            <div className="stf-recap">
              <div className="stf-kicker">Before you post</div>
              <p>
                Confirm the license class your state requires, size your patrol schedule from the site's stage and value at risk rather than a guess, and treat agency quotes as a ceiling. Staffoo shows you each guard's real rate before you confirm anyone.
              </p>
            </div>
          </article>

          {/* Sidebar Cards Column */}
          <aside className="stf-seo-aside">
            <div className="stf-toc">
              <h4>On this page</h4>
              <a href="#license">Do you need a licensed guard?</a>
              <a href="#how-many">How much cover you need</a>
              <a href="#cost">What it costs</a>
              <a href="#vs">Static guard vs mobile patrol</a>
              <a href="#checklist">Pre booking checklist</a>
              <a href="#direct">Direct vs agency</a>
            </div>

            <div className="stf-side-cta">
              <h4>Know what you need?</h4>
              <p>Post the job free and start receiving applications from licensed site security guards near you.</p>
              <a href="#" className="stf-btn stf-btn-solid stf-btn-block">Post a construction security job</a>
            </div>

            <div className="stf-aside-module">
              <h4>Related guides</h4>
              <a href="#">Licensing explained by state</a>
              <a href="#">How pricing works</a>
              <a href="#">Hiring for a business or agency</a>
              <a href="#">Residential &amp; estate security</a>
            </div>

            <div className="stf-aside-trust">
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Every applicant's license is checked before you see their profile.
              </div>
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                </svg>
                Free to post, no lock in contract, pay after sign off.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
